import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, FormControlLabel, Radio, Button, Paper, Collapse } from '@mui/material';
import { CreditCard, QrCode } from 'lucide-react';
import SimpleBackdrop from '../../components/Loading/SimpleBackdrop';
import { AlertInfo } from '../../components/Alerts/Alert';
import { displayMoney } from '../../utilities/displayValues';

import CreditCardForm from './CreditCardForm';
import { useFormPayment } from './FormPayment';
import PixPaymentModal from './PixPaymentModal';
import { api } from '../../services/api';

interface CheckoutProps {
    selectedPlan: string | null;
    planPrice: number;
    channel?: string;
}

const Checkout: React.FC<CheckoutProps> = ({ selectedPlan, planPrice, channel = '/license' }) => {
    const navigate = useNavigate();
    const [paymentMethod, setPaymentMethod] = useState('credit');
    const [openLoad, setOpenLoad] = useState(false);
    const [openModalPix, setOpenModalPix] = useState(false);
    const [payment_id, setPaymentId] = useState<string | null>(null);
    const [pixCode, setPixCode] = useState('');

    async function handlePayment() {
        setOpenLoad(true);
        try {
            if (paymentMethod === 'pix') {
                const data = { price: planPrice, id_plano_pagamento: selectedPlan };
                const response = await api.post(`/createpix`, data);
                console.log(response);
                if (response.data && response.data.success) {
                    setPixCode(response.data.payload);
                    setPaymentId(response.data.paymentId);
                    setOpenModalPix(true);
                }
            } else if (paymentMethod === 'credit') {
                const data = { price: planPrice, id_plano_pagamento: selectedPlan, ...formik.values };
                const response = await api.post(`/paymentcreditcard`, data);
                if (response.data && response.data.success) {
                    AlertInfo({ icon: 'success', title: response.data.msg, text: response.data.details }).then(() => {
                        sessionStorage.removeItem('isDisabled');
                        navigate(channel);
                    });
                } else {
                    console.log(response);
                    const text = response.data.details.errors[0].description || '';
                    AlertInfo({ icon: 'error', title: response.data.msg, text });
                }
            }
        } catch (error) {
            console.log(error);
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    const formik = useFormPayment(handlePayment);

    const paymentMethods = [
        { id: 'pix', label: 'Pagar com PIX', icon: QrCode },
        { id: 'credit', label: 'Pagar com Cartão de Crédito', icon: CreditCard },
    ];

    if (!selectedPlan) return null;

    const showCreditCardForm = paymentMethod === 'credit' || paymentMethod === 'subscription';

    return (
        <Paper sx={{ mt: 4, p: 3, borderRadius: 2 }}>
            <SimpleBackdrop open={openLoad} />
            <PixPaymentModal
                open={openModalPix}
                onClose={() => setOpenModalPix(false)}
                pixCode={pixCode}
                paymentId={payment_id}
                channel={channel}
            />

            <Typography variant="h6" gutterBottom>
                Finalizar Compra
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {paymentMethods.map(({ id, label, icon: Icon }) => (
                    <Box key={id}>
                        <FormControlLabel
                            value={id}
                            control={
                                <Radio
                                    checked={paymentMethod === id}
                                    onChange={(e) => setPaymentMethod(id)}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon size={20} />
                                    <Typography>{label}</Typography>
                                </Box>
                            }
                            sx={{
                                border: '1px solid',
                                borderColor: paymentMethod === id ? 'primary.main' : 'grey.300',
                                borderRadius: 1,
                                p: 1,
                                width: '100%',
                                '&:hover': {
                                    bgcolor: 'grey.50',
                                },
                            }}
                        />
                        <Collapse in={showCreditCardForm && paymentMethod === id}>
                            <Box sx={{ pl: 4, pr: 2, pt: 2 }}>
                                <CreditCardForm isSubscription={selectedPlan === '1'} formik={formik} />
                            </Box>
                        </Collapse>
                    </Box>
                ))}
            </Box>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                        Total a pagar:
                    </Typography>
                    <Typography variant="h5" color="primary.main" fontWeight="bold">
                        R$ {displayMoney(planPrice)}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    size="large"
                    sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                    }}
                    onClick={() => {
                        if (paymentMethod === "credit") formik.handleSubmit();
                        else handlePayment();
                    }}
                >
                    Finalizar Pagamento
                </Button>
            </Box>
        </Paper>
    );
};

export default Checkout;